<template>
  <div class="form-row"
    v-bind:class="{'has-error': errors.has(field.key)}">
    <div class="col-12 position-relative">
      <InputText
        v-bind:field="_formFields[0]"
        v-bind:value="addressData.address_line_1"
        v-on:input="handleAddressInputWithFeatureCheck"
        ref="addressInput"/>
      <div
        v-if="isAddressAutocompleteEnabled"
        class="address-suggestions"
        :style="{ display: showSuggestions ? 'block' : 'none' }"
        ref="suggestionsContainer">
        <div v-if="suggestions.length === 0" style="padding: 1rem; color: #666;">
          No suggestions found
        </div>
        <button
          v-for="(suggestion, index) in suggestions"
          :key="index"
          type="button"
          class="dropdown-item"
          :class="{ active: index === activeSuggestionIndex }"
          @click="selectSuggestion(suggestion)">
          {{ suggestion.text }}
        </button>
      </div>
    </div>
    <InputText class="col-12"
      v-bind:field="_formFields[1]"
      v-bind:value="addressData.address_line_2"
      v-on:input="updateField('address_line_2', $event)"/>
    <InputText class="col-6"
      v-bind:field="_formFields[2]"
      v-bind:value="addressData.city"
      v-on:input="updateField('city', $event)"/>
    <StateSelector class="col-3"
      v-bind:field="_formFields[3]"
      v-bind:value="addressData.state"
      v-on:input="updateField('state', $event)"/>
    <InputText class="col-3"
      v-bind:field="_formFields[4]"
      v-bind:value="addressData.zip_code"
      v-on:input="updateField('zip_code', $event)"/>
    <div class="col-12 form-group invalid-feedback"
      v-if="errors.has(field.key)">
      <span v-html="errors.first(field.key)"></span>
    </div>
  </div>
</template>

<script>
import CmsMixin from '@/mixins/CmsMixin';
import InputMixin from '@/mixins/InputMixin';
import FormMixin from '@/mixins/FormMixin';
import InputText from '@/components/inputs/simple/InputText.vue';
import InputNumber from '@/components/inputs/simple/InputNumber.vue';
import StateSelector from '@/components/inputs/complex/USStateSelector/USStateSelector.vue';

/**
 * Input Address
 * -- Implements v-model
 *
 * A field that renders a set of address related fields:
 *  • Address Line 1 {InputText}
 *  • Address Line 2 {InputText}
 *  • City {InputText}
 *  • State {USStateSelector}
 *  • ZIP {InputText}
 */
export default {
  name: 'InputAddress',
  components: {
    InputText,
    InputNumber,
    StateSelector,
  },
  mixins: [
    InputMixin,
    FormMixin,
    CmsMixin,
  ],
  props: {
    /**
     * The value object
     */
    value: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      /**
       * The local store of the value prop (prevents race conditions).
       */
      addressData: Object.assign({}, this.value),
      /**
       * The configuration for the address fields.
       */
      formFields: [
        {
          title: 'Address Line 1',
          key: 'address_line_1',
          required: true,
          disabled: this.field?.disabled,
          help_text: this.field?.help_text,
        },
        {
          title: 'Address Line 2',
          key: 'address_line_2',
          required: false,
          disabled: this.field?.disabled,
        },
        {
          title: 'City',
          key: 'city',
          required: true,
          disabled: this.field?.disabled,
        },
        {
          title: 'State',
          key: 'state',
          min_length: 2,
          max_length: 2,
          required: true,
          disabled: this.field?.disabled,
        },
        {
          title: 'ZIP',
          key: 'zip_code',
          regex_pattern: /^\d{5}(?:[-\s]\d{4})?$/,
          required: true,
          disabled: this.field?.disabled,
        },
      ],
      /**
       * Address autocomplete state
       */
      suggestions: [],
      showSuggestions: false,
      activeSuggestionIndex: -1,
      debounceTimer: null,
    };
  },
  computed: {
    /**
     * Modifies the form fields to get input titles from CMS.
     */
    _formFields() {
      return this.formFields.map((field) => {
        const titledField = Object.assign({}, field);
        titledField.title = this.getCMSText(field.key, {}, field.title);
        return titledField;
      });
    },
    /**
     * Check if address autocomplete feature is enabled
     */
    isAddressAutocompleteEnabled() {
      const enabledFeatures = this.$store.state.siteSettings?.settings?.rebateConfig?.enabledFeatures;
      return enabledFeatures && enabledFeatures.includes('verification_form_address_autocomplete');
    },
  },
  methods: {
    /**
     * Update the local store for value with new data and emit it to the
     * parent component.
     * @param key {String} the key to update
     * @param value {String} the new value of the key
     */
    updateField(key, value) {
      this.addressData[key] = value;
      this.errors.remove('address');
      this.$emit('input', this.addressData);
    },
    /**
     * Handle address input with feature flag check
     */
    handleAddressInputWithFeatureCheck(value) {
      if (this.isAddressAutocompleteEnabled) {
        this.handleAddressInput(value);
      } else {
        this.updateField('address_line_1', value);
      }
    },
    /**
     * Handle address input with autocomplete functionality
     */
    handleAddressInput(value) {
      this.updateField('address_line_1', value);

      const query = value.trim();

      // Clear previous timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Hide suggestions if query is too short
      if (query.length < 3) {
        this.hideSuggestions();
        return;
      }

      // Debounce the API call
      this.debounceTimer = setTimeout(() => {
        this.fetchAddressSuggestions(query);
      }, 200);
    },
    /**
     * Fetch address suggestions from API
     */
    async fetchAddressSuggestions(query) {
      try {
        const response = await fetch(`/api/1.0/applications/address-autocomplete/?q=${encodeURIComponent(query)}`, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        const data = await response.json();

        if (data.error) {
          console.error('Address autocomplete error:', data.error);
          this.hideSuggestions();
          return;
        }

        this.displaySuggestions(data.suggestions);
      } catch (error) {
        console.error('Address autocomplete fetch error:', error);
        this.hideSuggestions();
      }
    },
    /**
     * Display address suggestions
     */
    displaySuggestions(suggestions) {
      if (!suggestions || suggestions.length === 0) {
        this.hideSuggestions();
        return;
      }

      this.suggestions = suggestions;
      this.showSuggestions = true;
      this.activeSuggestionIndex = -1;
    },
    /**
     * Hide suggestions dropdown
     */
    hideSuggestions() {
      this.showSuggestions = false;
      this.activeSuggestionIndex = -1;
    },
    /**
     * Handle keyboard navigation
     */
    handleKeydown(event) {
      if (!this.showSuggestions || this.suggestions.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeSuggestionIndex = this.activeSuggestionIndex < this.suggestions.length - 1
          ? this.activeSuggestionIndex + 1
          : 0;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeSuggestionIndex = this.activeSuggestionIndex > 0
          ? this.activeSuggestionIndex - 1
          : this.suggestions.length - 1;
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.activeSuggestionIndex >= 0) {
          this.selectSuggestion(this.suggestions[this.activeSuggestionIndex]);
        }
      } else if (event.key === 'Escape') {
        this.hideSuggestions();
      }
    },
    /**
     * Select a suggestion and populate form fields
     */
    selectSuggestion(suggestion) {
      // Fill in the address line 1
      this.updateField('address_line_1', suggestion.street_line || '');

      // Fill in city if available
      if (suggestion.city) {
        this.updateField('city', suggestion.city);
      }

      // Fill in state if available
      if (suggestion.state) {
        this.updateField('state', suggestion.state);
      }

      // Fill in ZIP code if available
      if (suggestion.postal_code) {
        this.updateField('zip_code', suggestion.postal_code);
      }

      this.hideSuggestions();
    },
    /**
     * Handle click outside to hide suggestions
     */
    handleClickOutside(event) {
      const addressInput = this.$refs.addressInput?.$el?.querySelector('input');
      const suggestionsContainer = this.$refs.suggestionsContainer;

      if (addressInput && suggestionsContainer) {
        if (!addressInput.contains(event.target) && !suggestionsContainer.contains(event.target)) {
          this.hideSuggestions();
        }
      }
    },
  },
  watch: {
    /**
     * Update the local store for value with the new object.
     * @param newVal {Object} the new object to assign
     */
    value(newVal) {
      this.addressData = Object.assign({}, newVal);
    },
  },
  mounted() {
    // Only set up autocomplete functionality if feature is enabled
    if (this.isAddressAutocompleteEnabled) {
      // Hide suggestions when clicking outside
      document.addEventListener('click', this.handleClickOutside);

      // Add keydown listener to the actual input element
      this.$nextTick(() => {
        const addressInput = this.$refs.addressInput?.$el?.querySelector('input');
        if (addressInput) {
          addressInput.addEventListener('keydown', this.handleKeydown);
          addressInput.setAttribute('autocomplete', 'off');
        }
      });
    }
  },
  beforeDestroy() {
    // Clean up autocomplete functionality if it was enabled
    if (this.isAddressAutocompleteEnabled) {
      // Clean up event listener
      document.removeEventListener('click', this.handleClickOutside);

      // Clean up keydown listener
      const addressInput = this.$refs.addressInput?.$el?.querySelector('input');
      if (addressInput) {
        addressInput.removeEventListener('keydown', this.handleKeydown);
      }

      // Clean up debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    }
  },
};
</script>

<style scoped>
.position-relative {
  position: relative;
}

.address-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  width: 100%;
}

.address-suggestions .dropdown-item {
  padding: 0.5rem 1rem;
  border: none;
  border-bottom: 1px solid #dee2e6;
  background: none;
  text-align: left;
  width: 100%;
  cursor: pointer;
  display: block;
  color: #333;
}

.address-suggestions .dropdown-item:last-child {
  border-bottom: none;
}

.address-suggestions .dropdown-item:hover,
.address-suggestions .dropdown-item.active {
  background-color: #e9ecef;
}

.address-suggestions .dropdown-item:focus {
  outline: none;
  background-color: #e9ecef;
}
</style>
